import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import DOMPurify from "isomorphic-dompurify";
import { observer } from "mobx-react";
import { Anchor } from "../../components/anchor";
import { IconLocation } from "../../components/icons/icon-location";
import { IconWhatsapp } from "../../components/icons/icon-whatsapp";
import { SellerProfile } from "../../stores/seller-store/seller-store.model";
import { normalizePhoneNumber } from "../../utils/number";
import styles from "./store-info.module.css";
import { useStores } from "../../stores";
import { SellerPageRouter } from "../seller/page-router.props";
import { ImageViewer } from "../product-detail/components/image-carousel/image-viewer";

interface Props {
  sellerData: SellerProfile;
}

export const CredimartInfoBody = observer(({ sellerData }: Props) => {
  const { sellerStore, coverageAreaStore } = useStores();
  const router: SellerPageRouter = useRouter();
  const storeName = useMemo(() => router.query.seller, [router.query.seller]);
  const [showImageViewer, setShowImageViewer] = useState<boolean>(false);

  useEffect(() => {
    const { seller: sellerUsername } = router.query;
    if (sellerUsername && !sellerData) {
      sellerStore?.getSellerProfile({ username: String(sellerUsername) });
    } else {
      sellerStore?.setSellerProfile(sellerData);
    }

    async function init() {
      await coverageAreaStore.getSavedCoverageArea();
    }

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.seller]);

  const renderMainInfo = () => (
    <div className="flex items-center">
      {sellerData?.profilePicture ? (
        <img
          src={sellerData?.profilePicture}
          alt={sellerData?.User?.name}
          width={64}
          height={64}
          className="w-16 h-16 rounded-full bg-fill-gray drop-shadow-sm cursor-pointer"
          onClick={() => setShowImageViewer(true)}
          id="seller-info-img-profile"
          data-testid="seller-info-img-profile"
        />
      ) : (
        <div className="w-16 h-16 rounded-full drop-shadow-sm overflow-hidden cursor-pointer">
          <img
            src="/img/common/default-merchant.svg"
            alt={sellerData?.User?.name}
            width={64}
            height={64}
            onClick={() => setShowImageViewer(true)}
            id="seller-info-img-profile"
            data-testid="seller-info-img-profile"
          />
        </div>
      )}
      <div className="ml-4 flex-1">
        <h1
          className="text-title-md font-bold line-clamp-3 mb-2"
          id="seller-info-store-name"
          data-testid="seller-info-store-name"
          title={sellerData?.User?.name}
        >
          {sellerData?.User?.name}
        </h1>
        {sellerData?.latitude && sellerData?.longitude && (
          <Anchor
            className="text-primary"
            href={`https://www.google.com/maps/search/?api=1&query=${sellerData?.latitude},${sellerData?.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <IconLocation className="mr-1.5" width={16} height={16} />
            {coverageAreaStore?.selectedCity?.name ? (
              <span className="text-label-sm">
                {coverageAreaStore?.selectedCity?.name}
              </span>
            ) : (
              !!sellerData?.City?.name && (
                <span className="text-label-sm">{sellerData?.City?.name}</span>
              )
            )}
          </Anchor>
        )}
      </div>
      <Anchor
        href={`https://wa.me/${normalizePhoneNumber(
          coverageAreaStore?.selectedCoverageArea?.phone ??
          sellerData?.User?.phone
        )}?text=Hai%20${storeName}!`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex justify-center items-center w-12 h-12 p-2.5 bg-white rounded-full drop-shadow-lg hover:opacity-75 text-primary"
        id="seller-info-wa-btn"
      >
        <IconWhatsapp width={24} height={24} className="text-green" />
      </Anchor>
    </div>
  );

  const renderDescription = () => (
    <div className="min-h-[100px]">
      <h2 className="text-label-md font-bold">Deskripsi Toko</h2>
      {sellerData?.deliveryArea ? (
        <div
          className={styles.desc}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(sellerData?.deliveryArea),
          }}
        />
      ) : (
        "Belum ada deskripsi toko"
      )}
    </div>
  );

  return (
    <div className="mt-9 h-full bg-white">
      {renderMainInfo()}
      <hr />
      {renderDescription()}
      {showImageViewer && (
        <ImageViewer
          images={[
            {
              imageUrl: sellerData?.profilePicture
                ? sellerData?.profilePicture
                : "/img/common/default-merchant.svg",
              alt: sellerData?.User.name,
            },
          ]}
          onClose={() => setShowImageViewer(false)}
          initialIndex={0}
        />
      )}
    </div>
  );
});
